import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ArrowRight, 
  CheckCircle, 
  Zap, 
  Target, 
  Users, 
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Building2,
  TrendingUp,
  Bot,
  FileText,
  Calendar,
  MessageCircle
} from 'lucide-react';

const SolutionSection = ({ solution, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Product-specific icons
  const productIcons = {
    'envael-crm': Users,
    'envael-ai': Bot,
    'envael-automation': Zap,
    'envael-analytics': TrendingUp,
    'envael-forms': FileText,
    'envael-scheduler': Calendar,
    'envael-chat': MessageCircle
  };

  const IconComponent = productIcons[solution.id] || Building2;

  return (
    <section className={`py-24 ${index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content Side */}
          <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
            {/* Solution Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <IconComponent className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">{solution.title}</span>
            </div>

            {/* Title */}
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
              {solution.tagline}
            </h2>

            {/* Hero Summary */}
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              {solution.heroSummary}
            </p>

            {/* Benefits */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Target className="h-6 w-6 text-primary" />
                Key Benefits
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {solution.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground leading-relaxed">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button size="lg" className="group">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button variant="outline" size="lg" className="group">
                Learn More
                <ExternalLink className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              </Button>
            </div>

            {/* Pricing Note */}
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
              <p className="text-sm text-primary font-medium">
                💡 {solution.pricing}
              </p>
            </div>

            {/* Mock Demo/Screenshot Placeholder */}
            <div className="mt-8 p-6 rounded-lg bg-gradient-to-br from-muted/50 to-muted/30 border border-border/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm text-muted-foreground ml-2">Live Demo</span>
              </div>
              <div className="bg-background rounded-lg p-4 border border-border/30">
                <div className="text-center py-8">
                  <IconComponent className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-foreground mb-2">{solution.title} Demo</h4>
                  <p className="text-sm text-muted-foreground mb-4">Interactive preview coming soon</p>
                  <Button variant="outline" size="sm">
                    View Live Demo
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Details Side */}
          <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
            <Card className="group hover:shadow-2xl transition-all duration-500 border-border/50 hover:border-primary/30 overflow-hidden">
              <CardContent className="p-0">
                {/* Header */}
                <div className="p-8 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border/50">
                  <h3 className="text-2xl font-bold text-foreground mb-2">{solution.title}</h3>
                  <p className="text-muted-foreground">Tailored solutions for your industry</p>
                </div>

                {/* Expandable Content */}
                <div className="p-8">
                  {/* Deliverables */}
                  <div className="mb-8">
                    <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      What You Get
                    </h4>
                    <div className="space-y-3">
                      {solution.deliverables.slice(0, isExpanded ? solution.deliverables.length : 3).map((deliverable, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                          <span className="text-muted-foreground text-sm">{deliverable}</span>
                        </div>
                      ))}
                      {solution.deliverables.length > 3 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={toggleExpanded}
                          className="text-primary hover:text-primary/80 p-0 h-auto"
                        >
                          {isExpanded ? (
                            <>
                              Show Less <ChevronUp className="ml-1 h-4 w-4" />
                            </>
                          ) : (
                            <>
                              Show More <ChevronDown className="ml-1 h-4 w-4" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Integrations */}
                  <div className="mb-8">
                    <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-primary" />
                      Integrations
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {solution.integrations.map((integration, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {integration}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Industries */}
                  <div>
                    <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      Perfect For
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {solution.industries.map((industry, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {industry}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
